import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import PartnerDeliveryBlock from "@/components/PartnerDeliveryBlock";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import Modal from "@/components/Modal";
import TaskForm from "@/components/TaskForm";
import { FilePreviewModal, isPreviewableFile } from "@/components/FilePreviewModal";
import DeliverOrderModal from "@/components/DeliverOrderModal";
import PaymentsSection from "@/components/PaymentsSection";
import { formatDate } from "@/lib/dateUtils";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const STATUS_TONE = {
  design: "bg-slate-100 text-slate-800 border-slate-300",
  waiting_approval: "bg-amber-100 text-amber-900 border-amber-300",
  production: "bg-blue-100 text-blue-900 border-blue-300",
  ready: "bg-emerald-100 text-emerald-900 border-emerald-300",
  delivered: "bg-neutral-200 text-neutral-700 border-neutral-300",
};

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <div className="label-overline text-[var(--text-secondary)]">{title}</div>
      <div className="border border-[var(--border)] bg-white p-4">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-1 text-sm">
      <div className="col-span-1 font-mono-tech text-[11px] tracking-wider uppercase text-[var(--text-secondary)]">
        {label}
      </div>
      <div className="col-span-2 font-display">{value || "—"}</div>
    </div>
  );
}

/**
 * Small unobtrusive chip that attributes each order item to the pricing rule
 * that produced its price. Kept intentionally minimal — one line, muted colors —
 * so it doesn't compete with the item description or production details.
 *
 * Three states:
 *   • Rule snapshot present  → "Regla: NAME" as a clickable link that opens
 *     the Pricing Engine admin (if `onOpen` is provided).
 *   • Rule metadata absent AND item has a unit_price   → "Precio manual"
 *     (item created before the pricing engine existed, or the user typed
 *     the price directly without invoking a rule).
 *   • Nothing at all (services with zero price, imports, etc.) → "Sin regla".
 *
 * Historical items are NEVER recalculated. This chip is descriptive only.
 */
function PricingRuleChip({ item, onOpen, t, testId }) {
  const ruleId = item?.pricing_rule_id || null;
  const ruleName = item?.pricing_rule_name || null;
  const hasPrice = Number(item?.unit_price || 0) > 0;

  let label;
  let interactive = false;
  let title;

  if (ruleName || ruleId) {
    label = `${t("order_item_rule_label")}: ${ruleName || t("order_item_rule_unknown")}`;
    interactive = !!onOpen;
    title = t("order_item_rule_open_hint");
  } else if (hasPrice) {
    label = t("order_item_rule_manual");
    title = t("order_item_rule_manual_hint");
  } else {
    label = t("order_item_rule_none");
  }

  const handleClick = () => {
    if (!interactive) return;
    // We pass the id (may be null → landing on the panel is enough).
    onOpen(ruleId, ruleName);
  };

  const Base = interactive ? "button" : "span";
  return (
    <Base
      type={interactive ? "button" : undefined}
      onClick={interactive ? handleClick : undefined}
      title={title}
      data-testid={testId}
      className={[
        "mt-1 inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-wider",
        "px-1.5 py-0.5 border",
        interactive
          ? "border-[var(--accent-cyan,#0E5FFF)]/40 text-[var(--accent-cyan,#0E5FFF)] hover:bg-[var(--accent-cyan,#0E5FFF)] hover:text-white transition-colors cursor-pointer"
          : "border-[var(--border)] text-[var(--text-secondary)] cursor-default",
      ].join(" ")}
    >
      <span aria-hidden>◆</span>
      <span className="uppercase tracking-wider">{label}</span>
      {interactive && <span aria-hidden>↗</span>}
    </Base>
  );
}

function TaskListItem({ tk, lang, t, employeesById = {} }) {
  // Sprint 4 · Group B — Show the assigned employee for every task, both in
  // the per-item task list and the general order-level task list, so the WO
  // detail modal matches the coherence promised by the Orders row counter.
  const assigneeName = tk.employee_id
    ? employeesById[tk.employee_id]?.name || null
    : null;
  return (
    <li
      key={tk.id}
      data-testid={`order-detail-task-${tk.id}`}
      className="py-2 flex items-center justify-between gap-3"
    >
      <div className="min-w-0">
        <div className="font-display font-bold truncate">{tk.name}</div>
        <div className="flex items-center gap-2 flex-wrap mt-0.5">
          <span className="font-mono-tech text-[11px] text-[var(--text-secondary)]">
            {formatDate(tk.due_date, lang)}
          </span>
          {tk.task_type && (
            <span className="inline-block border border-[var(--border)] bg-[var(--surface-alt,#f5f5f5)] px-1.5 py-0.5 text-[10px] font-display font-bold tracking-wider uppercase">
              {tk.task_type}
            </span>
          )}
          <span
            data-testid={`order-detail-task-assignee-${tk.id}`}
            className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-display font-bold tracking-wide ${
              assigneeName
                ? "border-[var(--border)] bg-white text-[var(--text-primary)]"
                : "border-dashed border-[var(--border)] bg-transparent text-[var(--text-secondary)] italic"
            }`}
            title={assigneeName || t("unassigned")}
          >
            <span aria-hidden>👤</span>
            {assigneeName || t("unassigned")}
          </span>
        </div>
      </div>
      <span
        data-testid={`order-detail-task-status-${tk.id}`}
        className={`text-[10px] font-display font-bold tracking-wide border px-2 py-0.5 whitespace-nowrap ${
          tk.status === "completed"
            ? "bg-emerald-100 border-emerald-300 text-emerald-900"
            : tk.status === "in_progress"
            ? "bg-blue-100 border-blue-300 text-blue-900"
            : "bg-slate-100 border-slate-300 text-slate-800"
        }`}
      >
        {t(`task_status_${tk.status}`)}
      </span>
    </li>
  );
}

export default function OrderDetailsModal({
  open,
  order,
  onClose,
  employees = [],
  onTaskChanged,
  onChanged,
  onOpenPricingRule,
}) {
  const { t, lang } = useI18n();
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Sprint 4 · Group B — Fast UUID → employee record lookup for the
  // TaskListItem assignee chip (Issue #6).
  const employeesById = useMemo(() => {
    const m = {};
    for (const e of employees) if (e?.id) m[e.id] = e;
    return m;
  }, [employees]);

  // Per-item task creation modal state
  const [taskModal, setTaskModal] = useState({ open: false, item: null });

  // Sprint 5.3 — V2 catalog lookup maps for displaying readable names
  const [decoLookup, setDecoLookup] = useState({});
  const [locLookup, setLocLookup] = useState({});

  // Sprint O4 — Deliver modal + local-overriding order copy so payments refresh
  // immediately without forcing the parent to refetch the entire list.
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [localOrder, setLocalOrder] = useState(order);
  useEffect(() => {
    setLocalOrder(order);
  }, [order]);
  const refreshOrder = async () => {
    if (!order?.id) return;
    try {
      const { data } = await axios.get(`${API}/orders`);
      const found = (data || []).find((o) => o.id === order.id);
      if (found) setLocalOrder(found);
    } catch {
      /* fall back to parent's onChanged */
    }
    onChanged?.();
  };

  const handleDuplicate = async () => {
    if (!order?.id) return;
    if (!window.confirm("Crear una copia de esta orden?")) return;
    try {
      const { data } = await axios.post(`${API}/orders/${order.id}/duplicate`);
      toast.success(`Orden duplicada: #${data.invoice_number}`);
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  // Feb 2026 · restaurar el control de estado a nivel de Work Order.
  // El backend deriva `order.status` del mínimo `production_status` de los
  // items (`_recompute_order_status`), así que aplicamos el nuevo estado a
  // TODOS los items vía `PATCH /orders/{id}/items/{itemId}/production-status`.
  // El estado `delivered` conserva su botón dedicado ("Marcar como entregada")
  // porque requiere validaciones de pago y flujo de firma en `DeliverOrderModal`.
  const ORDER_STATUS_TO_ITEM_PS = {
    design: "design",
    production: "in_production",
    ready: "ready_for_delivery",
  };
  const [statusSaving, setStatusSaving] = useState(false);
  const handleChangeOrderStatus = async (nextStatus) => {
    const ps = ORDER_STATUS_TO_ITEM_PS[nextStatus];
    if (!ps || !localOrder?.id) return;
    const currentItems = Array.isArray(localOrder.items) ? localOrder.items : [];
    if (currentItems.length === 0) {
      toast.error("La orden no tiene ítems");
      return;
    }
    setStatusSaving(true);
    try {
      let latest = null;
      for (const it of currentItems) {
        if (!it?.id) continue;
        const { data } = await axios.patch(
          `${API}/orders/${localOrder.id}/items/${it.id}/production-status`,
          { production_status: ps },
          { withCredentials: true },
        );
        latest = data;
      }
      if (latest) setLocalOrder(latest);
      toast.success(t(`order_status_${nextStatus}`));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setStatusSaving(false);
    }
  };

  const handleAutoTasks = async (item) => {
    try {
      const { data } = await axios.post(
        `${API}/orders/${order.id}/items/${item.id}/auto-tasks`,
        {}
      );
      toast.success(`Generadas ${data.count} tareas (${data.method})`);
      await loadTasks(order.id);
      onTaskChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [dRes, lRes] = await Promise.all([
          axios.get(`${API}/decoration-methods`, { withCredentials: true }),
          axios.get(`${API}/logo-locations`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        const dm = {};
        (dRes.data || []).forEach((x) => { dm[x.id] = x.name; });
        const lm = {};
        (lRes.data || []).forEach((x) => { lm[x.id] = x.name; });
        setDecoLookup(dm);
        setLocLookup(lm);
      } catch {
        /* optional */
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const loadTasks = async (orderId) => {
    if (!orderId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tasks`);
      setTasks(data.filter((tk) => tk.order_id === orderId));
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!open || !order?.id) return;
    setLoading(true);
    axios
      .get(`${API}/tasks`)
      .then(({ data }) => {
        if (cancelled) return;
        setTasks(data.filter((tk) => tk.order_id === order.id));
      })
      .catch(() => setTasks([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, order?.id]);

  if (!order) return null;

  // GP 1.0 — Work Order detection. Work Orders (bridge from Invoice) must
  // NOT show financial info; that lives in the source Invoice.
  const isWorkOrder =
    Boolean(order.wo_number) ||
    String(order.invoice_number || "").startsWith("WO-");
  const woNumber = order.wo_number || order.invoice_number || "";
  const originInvoice = order.source_invoice_number || "";

  const items = Array.isArray(order.items) ? order.items : [];
  const itemIds = new Set(items.map((it) => it?.id).filter(Boolean));

  // Bucket tasks: per-item vs general
  const tasksByItem = {};
  const generalTasks = [];
  tasks.forEach((tk) => {
    if (tk.order_item_id && itemIds.has(tk.order_item_id)) {
      (tasksByItem[tk.order_item_id] ||= []).push(tk);
    } else {
      generalTasks.push(tk);
    }
  });

  const submitNewItemTask = async (payload) => {
    try {
      await axios.post(`${API}/tasks`, payload);
      toast.success(t("save"));
      setTaskModal({ open: false, item: null });
      await loadTasks(order.id);
      onTaskChanged && onTaskChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const canCreate = isAdmin && Array.isArray(employees);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isWorkOrder
          ? `Work Order · ${woNumber}${originInvoice ? ` · Origen: ${originInvoice}` : ""}`
          : `${t("order_details")} · #${order.invoice_number || "—"}`
      }
      testId={DASHBOARD.orderDetailsModal}
      wide
    >
      <div className="space-y-5">
        {/* Header status + due */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border)] pb-3">
          <div>
            <div className="label-overline text-[var(--text-secondary)]">
              {t("delivery_date")}
            </div>
            <div className="font-display text-2xl font-black tracking-tight">
              {formatDate(order.delivery_date, lang)}
            </div>
          </div>
          <span
            className={`border px-3 py-1 font-display font-bold tracking-wide text-xs ${
              STATUS_TONE[localOrder?.status || order.status] || "bg-slate-100 border-slate-300"
            }`}
          >
            {t(`order_status_${localOrder?.status || order.status}`)}
          </span>
          {/* Feb 2026 — Editar estado de Work Order (segmentado explícito).
              Visible sólo para admin y cuando la orden aún no está entregada.
              `delivered` se gestiona con el botón dedicado más abajo. */}
          {isAdmin && localOrder?.status !== "delivered" && (
            <div
              data-testid="order-status-editor"
              className="flex flex-col items-start gap-1"
            >
              <div className="label-overline text-[var(--text-secondary)]">
                Editar estado
              </div>
              <div className="inline-flex border-2 border-black">
                {[
                  { key: "design", label: t("order_status_design") },
                  { key: "production", label: t("order_status_production") },
                  { key: "ready", label: t("order_status_ready") },
                ].map((opt, idx, arr) => {
                  const active = (localOrder?.status || order.status) === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      data-testid={`order-status-btn-${opt.key}`}
                      disabled={statusSaving || active}
                      onClick={() => handleChangeOrderStatus(opt.key)}
                      className={[
                        "px-3 py-1.5 font-display font-bold text-xs tracking-wide transition-colors",
                        idx > 0 ? "border-l-2 border-black" : "",
                        active
                          ? "bg-black text-white cursor-default"
                          : "bg-white text-black hover:bg-black hover:text-white disabled:opacity-50",
                      ].join(" ")}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Bloque 5A — Traslado/entrega Partner Point (solo admin, se auto-oculta) */}
        {isAdmin && (localOrder || order)?.id && (
          <PartnerDeliveryBlock orderId={(localOrder || order).id} admin />
        )}

        {/* Sprint O2 + O4 — Quick actions */}
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            {/* GP 1.0 — Work Order actions: PDF + Print. No financial ops. */}
            {isWorkOrder && (
              <>
                <button
                  type="button"
                  data-testid="wo-detail-pdf-btn"
                  onClick={async () => {
                    try {
                      const res = await axios.get(
                        `${API}/commercial/work-orders/${order.id}/pdf`,
                        { responseType: "blob", withCredentials: true }
                      );
                      const url = URL.createObjectURL(res.data);
                      window.open(url, "_blank");
                    } catch {
                      toast.error("No se pudo generar el PDF");
                    }
                  }}
                  className="border-2 border-black bg-white px-3 py-1.5 font-display font-bold text-xs tracking-wide hover:bg-black hover:text-white transition-colors"
                >
                  📄 PDF
                </button>
                <button
                  type="button"
                  data-testid="wo-detail-print-btn"
                  onClick={async () => {
                    try {
                      const res = await axios.get(
                        `${API}/commercial/work-orders/${order.id}/pdf`,
                        { responseType: "blob", withCredentials: true }
                      );
                      const url = URL.createObjectURL(res.data);
                      const win = window.open(url, "_blank");
                      if (win) {
                        win.addEventListener("load", () => win.print());
                      }
                    } catch {
                      toast.error("No se pudo imprimir");
                    }
                  }}
                  className="border-2 border-black bg-white px-3 py-1.5 font-display font-bold text-xs tracking-wide hover:bg-black hover:text-white transition-colors"
                >
                  {t("action_print")}
                </button>
              </>
            )}
            {!isWorkOrder && (
              <button
                type="button"
                data-testid={DASHBOARD.duplicateOrderBtn(order.id)}
                onClick={handleDuplicate}
                className="border-2 border-black bg-white px-3 py-1.5 font-display font-bold text-xs tracking-wide hover:bg-black hover:text-white transition-colors"
              >
                ⎘ Duplicar orden
              </button>
            )}
            {order.status !== "delivered" && (
              <button
                type="button"
                data-testid={DASHBOARD.openDeliverModalBtn(order.id)}
                onClick={() => setDeliverOpen(true)}
                className="border-2 border-emerald-600 bg-emerald-50 text-emerald-900 px-3 py-1.5 font-display font-bold text-xs tracking-wide hover:bg-emerald-600 hover:text-white transition-colors"
              >
                {t("action_mark_delivered")}
              </button>
            )}
            {order.status === "delivered" && order.delivered_at && (
              <div className="font-mono-tech text-[0.7rem] tracking-wider text-emerald-700 self-center">
                ENTREGADA · {order.delivered_at.slice(0, 16).replace("T", " ")}
              </div>
            )}
          </div>
        )}

        <div className={`grid grid-cols-1 ${isWorkOrder ? "" : "md:grid-cols-2"} gap-4`}>
          <Section title={t("customer_info")}>
            <Row label={t("customer")} value={order.customer_name} />
            <Row label={t("company_name")} value={order.company_name} />
            <Row label={t("contact_person")} value={order.contact_person} />
            <Row label={t("customer_phone")} value={order.customer_phone} />
            {isWorkOrder ? (
              <>
                <Row label="Work Order #" value={woNumber} />
                <Row label="Invoice de Origen" value={originInvoice || "—"} />
              </>
            ) : (
              <Row label={t("invoice_number")} value={`#${order.invoice_number || "—"}`} />
            )}
          </Section>

          {!isWorkOrder && (
            <Section title={t("financial_info")}>
              <PaymentsSection order={localOrder || order} onChanged={refreshOrder} disabled={!isAdmin} />
            </Section>
          )}
        </div>

        <Section title="Archivos del cliente">
          <WorkOrderClientFiles orderId={order.id} />
        </Section>

        <Section title={t("products_list")}>
          {items.length === 0 && !order.product ? (
            <div className="text-sm text-[var(--text-secondary)] italic py-2">—</div>
          ) : items.length === 0 ? (
            <div className="text-sm py-1">{order.product}</div>
          ) : (
            <div className="space-y-4">
              {items.map((it, idx) => {
                const itemTasks = (it.id && tasksByItem[it.id]) || [];
                return (
                  <div
                    key={it.id || idx}
                    data-testid={`order-detail-item-${idx}`}
                    className="border border-[var(--border)] bg-[var(--surface-alt,#fafafa)]/40"
                  >
                    {/* Item header */}
                    <div className="px-4 py-3 border-b border-[var(--border)] flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {it.product_code && (
                            <span className="font-mono-tech text-[11px] tracking-wider uppercase text-[var(--text-secondary)]">
                              {it.product_code}
                            </span>
                          )}
                          <span className="label-overline text-[var(--text-secondary)]">
                            {t("quantity")}: <span className="font-mono-tech tabular-nums">{it.quantity ?? "—"}</span>
                          </span>
                        </div>
                        <div className="font-display font-bold text-base mt-0.5 break-words">
                          {it.description || "—"}
                        </div>
                        {/* Pricing rule attribution — sprint 2026-02-15.
                            Non-obtrusive: only rendered when a snapshot
                            exists on the item. Legacy items without any
                            pricing rule metadata show "Precio manual". */}
                        <PricingRuleChip
                          item={it}
                          onOpen={onOpenPricingRule}
                          t={t}
                          testId={`order-detail-item-rule-${idx}`}
                        />
                        {it.notes && (
                          <div className="text-xs text-[var(--text-secondary)] mt-1 whitespace-pre-wrap">
                            {it.notes}
                          </div>
                        )}
                        {/* Sprint 5.3 — V2 structured production summary (rendered only when present) */}
                        {(it.decoration_method_id || it.logo_location_id || it.colors_used || it.artwork_url || it.customer_notes || it.width || it.height || it.area_sqft) ? (
                          <div
                            data-testid={`order-detail-item-production-${idx}`}
                            className="mt-2 border-l-2 border-[var(--accent-cyan,#0E5FFF)] pl-3 space-y-1"
                          >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 text-[12px]">
                              {it.decoration_method_id && (
                                <div>
                                  <span className="font-mono-tech uppercase tracking-wider text-[var(--text-secondary)] mr-1">
                                    {t("field_decoration_method")}:
                                  </span>
                                  <span className="font-display font-bold">
                                    {decoLookup[it.decoration_method_id] || it.decoration_method_id}
                                  </span>
                                </div>
                              )}
                              {it.logo_location_id && (
                                <div>
                                  <span className="font-mono-tech uppercase tracking-wider text-[var(--text-secondary)] mr-1">
                                    {t("field_logo_location")}:
                                  </span>
                                  <span className="font-display font-bold">
                                    {locLookup[it.logo_location_id] || it.logo_location_id}
                                  </span>
                                </div>
                              )}
                              {it.colors_used && (
                                <div>
                                  <span className="font-mono-tech uppercase tracking-wider text-[var(--text-secondary)] mr-1">
                                    {t("field_colors_used")}:
                                  </span>
                                  <span className="font-display">{it.colors_used}</span>
                                </div>
                              )}
                              {it.artwork_url && (
                                <div>
                                  <span className="font-mono-tech uppercase tracking-wider text-[var(--text-secondary)] mr-1">
                                    {t("field_artwork_url")}:
                                  </span>
                                  <a
                                    href={it.artwork_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline text-[var(--accent-cyan,#0E5FFF)] break-all"
                                  >
                                    {it.artwork_url}
                                  </a>
                                </div>
                              )}
                              {(it.width || it.height) && (
                                <div>
                                  <span className="font-mono-tech uppercase tracking-wider text-[var(--text-secondary)] mr-1">
                                    {t("dimensions_section")}:
                                  </span>
                                  <span className="font-mono-tech tabular-nums font-bold">
                                    {it.width ?? "?"} × {it.height ?? "?"}
                                    {it.thickness ? ` × ${it.thickness}` : ""}
                                  </span>
                                  {it.area_sqft != null && (
                                    <span className="font-mono-tech tabular-nums text-[var(--text-secondary)] ml-2">
                                      = {it.area_sqft} ft²
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            {it.customer_notes && (
                              <div className="text-xs whitespace-pre-wrap mt-1">
                                <span className="font-mono-tech uppercase tracking-wider text-[var(--text-secondary)] mr-1">
                                  {t("field_customer_notes")}:
                                </span>
                                {it.customer_notes}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                      {canCreate && it.id && (
                        <div className="flex flex-col gap-1.5">
                          <button
                            type="button"
                            data-testid={`create-task-for-item-btn-${idx}`}
                            onClick={() => setTaskModal({ open: true, item: it })}
                            className="border-2 border-[var(--text-primary)] bg-white px-3 py-1.5 font-display font-bold text-xs tracking-wide hover:bg-[var(--text-primary)] hover:text-white transition-colors whitespace-nowrap"
                          >
                            {t("create_task_for_item")}
                          </button>
                          <button
                            type="button"
                            data-testid={DASHBOARD.autoTasksBtn(it.id)}
                            onClick={() => handleAutoTasks(it)}
                            className="border-2 border-amber-500 bg-amber-50 text-amber-900 px-3 py-1.5 font-display font-bold text-xs tracking-wide hover:bg-amber-500 hover:text-white transition-colors whitespace-nowrap"
                            title="Crear tareas según el método de decoración"
                          >
                            ⚡ Tareas auto
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Item tasks */}
                    <div className="px-4 py-3">
                      <div className="label-overline mb-1.5 text-[var(--text-secondary)]">
                        {t("item_tasks_label")}
                      </div>
                      {loading ? (
                        <div className="text-sm text-[var(--text-secondary)]">…</div>
                      ) : itemTasks.length === 0 ? (
                        <div
                          data-testid={`item-no-tasks-${idx}`}
                          className="text-sm text-[var(--text-secondary)] italic"
                        >
                          {t("no_item_tasks")}
                        </div>
                      ) : (
                        <ul
                          data-testid={`item-tasks-list-${idx}`}
                          className="divide-y divide-[var(--border)]"
                        >
                          {itemTasks.map((tk) => (
                            <TaskListItem key={tk.id} tk={tk} lang={lang} t={t} employeesById={employeesById} />
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        <Section title={t("general_order_tasks")}>
          {loading ? (
            <div className="text-sm text-[var(--text-secondary)]">…</div>
          ) : generalTasks.length === 0 ? (
            <div
              data-testid="general-tasks-empty"
              className="text-sm text-[var(--text-secondary)] italic"
            >
              {t("no_related_tasks")}
            </div>
          ) : (
            <ul
              data-testid="general-tasks-list"
              className="divide-y divide-[var(--border)]"
            >
              {generalTasks.map((tk) => (
                <TaskListItem key={tk.id} tk={tk} lang={lang} t={t} employeesById={employeesById} />
              ))}
            </ul>
          )}
        </Section>

        {order.notes && (
          <Section title={t("notes")}>
            <div className="text-sm whitespace-pre-wrap">{order.notes}</div>
          </Section>
        )}

        <div className="flex justify-end pt-2">
          <button
            data-testid="order-details-close"
            onClick={onClose}
            className="border border-[var(--border)] px-5 py-2 font-display font-bold hover:bg-[var(--surface-alt)]"
          >
            {t("close")}
          </button>
        </div>
      </div>

      {/* Per-item Task creation modal (admin only, only when employees prop is passed) */}
      {canCreate && (
        <Modal
          open={taskModal.open}
          onClose={() => setTaskModal({ open: false, item: null })}
          title={`${t("new_task")} · ${taskModal.item?.description || ""}`}
          testId="item-task-modal"
          wide
        >
          <TaskForm
            initial={{}}
            employees={employees}
            orders={[order]}
            lockedOrderId={order.id}
            lockedOrderItemId={taskModal.item?.id || null}
            lockedProduct={taskModal.item?.description || ""}
            onSubmit={submitNewItemTask}
            onCancel={() => setTaskModal({ open: false, item: null })}
          />
        </Modal>
      )}

      {/* Sprint O4 — Deliver order modal */}
      <DeliverOrderModal
        order={localOrder || order}
        open={deliverOpen}
        onClose={() => setDeliverOpen(false)}
        onDelivered={(updated) => {
          setLocalOrder(updated);
          onChanged?.();
        }}
      />
    </Modal>
  );
}

// Fase 2 · Archivos del cliente — SOLO consulta/descarga (sin subir/editar).
// `lazy` evita disparar un fetch por cada tarjeta en Tareas.
export function WorkOrderClientFiles({ orderId, lazy = false }) {
  const [files, setFiles] = useState(null); // null = aún no cargado
  const [open, setOpen] = useState(!lazy);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    if (!open || files !== null) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/commercial/work_orders/${orderId}/client-files`,
          { withCredentials: true }
        );
        setFiles(data);
      } catch {
        setFiles([]);
      }
    })();
  }, [open, files, orderId]);

  const download = async (f) => {
    try {
      const res = await axios.get(
        `${API}/commercial/work_orders/${orderId}/client-files/${f.file_id}/download`,
        { responseType: "blob", withCredentials: true }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename || f.name || "archivo";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el archivo");
    }
  };

  if (lazy && !open) {
    return (
      <button
        type="button"
        data-testid={`wo-files-toggle-${orderId}`}
        onClick={() => setOpen(true)}
        className="font-mono-tech text-[0.65rem] font-bold border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors"
      >
        Ver archivos del cliente
      </button>
    );
  }
  if (files === null) {
    return (
      <div className="font-mono-tech text-[10px] text-[var(--text-secondary)]">
        Cargando archivos…
      </div>
    );
  }
  if (files.length === 0) {
    return (
      <div
        data-testid={`wo-files-empty-${orderId}`}
        className="text-xs text-[var(--text-secondary)] italic"
      >
        Sin archivos del cliente
      </div>
    );
  }
  return (
    <div
      data-testid={`wo-files-list-${orderId}`}
      className="border border-[var(--border)] divide-y divide-[var(--border)] bg-white"
    >
      {files.map((f) => (
        <div
          key={f.file_id}
          data-testid={`wo-file-row-${f.file_id}`}
          className="p-2 flex items-start gap-2 text-sm"
        >
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate">{f.name}</div>
            <div className="font-mono-tech text-[10px] text-[var(--text-secondary)] truncate">
              {f.original_filename} · {Math.max(1, Math.round((f.size || 0) / 1024))} KB
            </div>
            {f.note && (
              <div
                data-testid={`wo-file-note-${f.file_id}`}
                className="text-xs mt-0.5 whitespace-pre-wrap"
              >
                {f.note}
              </div>
            )}
          </div>
          <button
            type="button"
            data-testid={`wo-file-view-${f.file_id}`}
            onClick={() => setPreviewFile(f)}
            className="font-mono-tech text-[0.65rem] font-bold border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors"
            style={{ display: isPreviewableFile(f) ? undefined : "none" }}
          >
            Ver
          </button>
          <button
            type="button"
            data-testid={`wo-file-download-${f.file_id}`}
            onClick={() => download(f)}
            className="font-mono-tech text-[0.65rem] font-bold border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors"
          >
            Descargar
          </button>
        </div>
      ))}
      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          downloadUrl={`${API}/commercial/work_orders/${orderId}/client-files/${previewFile.file_id}/download`}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
}
